import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getCashRegisters,
  createCashRegister,
  updateCashRegister,
  deactivateCashRegister,
  setCashRegisterUsers,
  updateCashRegisterClientTypes,
  updateCashRegisterDiscountApprover,
  updateCashRegisterMaxOpenSales,
  getUsers,
  getBranchUsers,
} from "../api";
import CashRegisterForm from "../components/CashRegisterForm";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import useConfirmDialog from '../hooks/useConfirmDialog';

const CashRegistersPage = ({ branchId: branchIdProp, embedded = false }) => {
  const params = useParams();
  const resolvedBranchId = branchIdProp || params.branchId || localStorage.getItem("selectedBranchId");
  const [cashRegisters, setCashRegisters] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [branchUsers, setBranchUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingRegister, setEditingRegister] = useState(null);
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const canManageRegisters = hasPermission(user, "cash_register.manage");

  const availableUsers = useMemo(() => {
    const branchUserIds = new Set(branchUsers.map((entry) => entry.userId));
    return allUsers
      .filter((user) => hasPermission(user, "branch.access.any") || branchUserIds.has(user.id))
      .sort((a, b) => (a.name || a.username || "").localeCompare(b.name || b.username || ""));
  }, [allUsers, branchUsers]);
  const defaultRegisterName = useMemo(
    () => `Caja ${cashRegisters.length + 1}`,
    [cashRegisters.length]
  );

  const fetchData = async () => {
    if (!resolvedBranchId) return;
    setLoading(true);
    setError("");
    try {
      const [registers, users, branch] = await Promise.all([
        getCashRegisters(resolvedBranchId, { pageSize: 0 }),
        getUsers({ pageSize: 0 }),
        getBranchUsers(resolvedBranchId, { pageSize: 0 }),
      ]);
      setCashRegisters(normalizePaginatedResponse(registers).data || []);
      setAllUsers(normalizePaginatedResponse(users).data || []);
      setBranchUsers(normalizePaginatedResponse(branch).data || []);
    } catch (err) {
      setError(err.message || "Error cargando cajas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [resolvedBranchId]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingRegister(null);
  };

  const handleSave = async (payload) => {
    if (!canManageRegisters) return;
    if (!resolvedBranchId) return;
    setSaving(true);
    setError("");
    try {
      if (editingRegister) {
        await updateCashRegister(resolvedBranchId, editingRegister.id, {
          name: payload.name,
          isActive: payload.isActive,
        });
        await updateCashRegisterClientTypes(
          resolvedBranchId,
          editingRegister.id,
          payload.allowedClientTypes || []
        );
        await updateCashRegisterDiscountApprover(
          resolvedBranchId,
          editingRegister.id,
          payload.discountApproverUserId
        );
        await updateCashRegisterMaxOpenSales(
          resolvedBranchId,
          editingRegister.id,
          payload.maxOpenSales
        );
        await setCashRegisterUsers(
          resolvedBranchId,
          editingRegister.id,
          payload.users || []
        );
      } else {
        await createCashRegister(resolvedBranchId, payload);
      }
      await fetchData();
      closeForm();
    } catch (err) {
      setError(err.message || "Error guardando caja.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (register) => {
    if (!canManageRegisters) return;
    if (!resolvedBranchId || !register) return;
    const approved = await confirm({
      title: 'Desactivar caja',
      message: `Desactivar la caja "${register.name}"?`,
      confirmLabel: 'Desactivar',
      cancelLabel: 'Cancelar',
      tone: 'warning',
    });
    if (!approved) return;
    setSaving(true);
    setError("");
    try {
      await deactivateCashRegister(resolvedBranchId, register.id);
      await fetchData();
    } catch (err) {
      setError(err.message || "Error desactivando caja.");
    } finally {
      setSaving(false);
    }
  };

  const formatUsers = (register) => {
    const users = register?.CashRegisterUsers || [];
    if (!users.length) return "Sin usuarios";
    const names = users
      .filter((entry) => entry?.isActive !== false)
      .map((entry) => entry?.User?.name || entry?.User?.username || entry.userId)
      .filter(Boolean);
    return names.length ? names.join(", ") : "Sin usuarios";
  };

  const formatClientTypes = (register) => {
    const types = Array.isArray(register?.allowedClientTypes) ? register.allowedClientTypes : [];
    return types.length ? types.join(", ") : "Sin limites";
  };

  const resolveApprover = (register) => {
    const approverId = register?.discountApproverUserId;
    if (!approverId) return "Sin encargado";
    const user = availableUsers.find((item) => item.id === approverId);
    return user ? user.name || user.username : `Usuario ${approverId}`;
  };

  if (loading) {
    return (
      <div className={embedded ? "text-sm text-gray-500" : "p-6"}>
        <InlineLoading label="Cargando cajas" />
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "p-6 max-w-5xl mx-auto"}>
      <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${embedded ? "" : "mb-6"}`}>
        <div>
          <h1 className={embedded ? "text-lg font-semibold" : "text-2xl font-bold"}>
            Cajas registradoras
          </h1>
          <p className="text-sm text-gray-600">
            Configura accesos, tipos de cliente y limites por caja.
          </p>
        </div>
        {canManageRegisters && (
          <button
            onClick={() => {
              setEditingRegister(null);
              setFormOpen(true);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Nueva caja
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {cashRegisters.map((register) => (
          <div key={register.id} className="bg-white rounded shadow p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold">{register.name}</h2>
                <p className="text-sm text-gray-600">
                  Estado: {register.isActive ? "Activa" : "Inactiva"}
                </p>
              </div>
              <div className="flex gap-2">
                {canManageRegisters && (
                  <button
                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                    onClick={() => {
                      setEditingRegister(register);
                      setFormOpen(true);
                    }}
                  >
                    Editar
                  </button>
                )}
                {canManageRegisters && register.isActive && (
                  <button
                    className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm"
                    onClick={() => handleDeactivate(register)}
                    disabled={saving}
                  >
                    Desactivar
                  </button>
                )}
              </div>
            </div>

            <div className="mt-3 text-sm text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-2">
              <p>
                <strong>Tipos de cliente:</strong> {formatClientTypes(register)}
              </p>
              <p>
                <strong>Encargado descuentos:</strong> {resolveApprover(register)}
              </p>
              <p>
                <strong>Limite ventas abiertas:</strong> {register.maxOpenSales ?? 5}
              </p>
              <p>
                <strong>Usuarios:</strong> {formatUsers(register)}
              </p>
              {register.activeSession && (
                <p className="md:col-span-2 text-amber-600">
                  Sesion abierta desde {new Date(register.activeSession.openedAt).toLocaleString("es-CL")}
                </p>
              )}
            </div>
          </div>
        ))}
        {!cashRegisters.length && (
          <div className="bg-white rounded shadow p-6 text-sm text-gray-600">
            No hay cajas registradoras configuradas.
          </div>
        )}
      </div>

      <CashRegisterForm
        isOpen={formOpen}
        onClose={closeForm}
        onSave={handleSave}
        initialData={editingRegister}
        defaultName={defaultRegisterName}
        availableUsers={availableUsers}
        isSaving={saving}
      />
    </div>
  );
};

export default CashRegistersPage;
